import React from 'react';
import { contactInfo } from '../data/mock';
import { useInView } from '../hooks/useInView';
import { useLanguage } from '../context/LanguageContext';
import { MapPin, Phone, Clock, Instagram } from 'lucide-react';

const ContactSection = () => {
  const { t } = useLanguage();
  const [ref, isInView] = useInView({ threshold: 0.1 });

  return (
    <section
      id="kontakt"
      ref={ref}
      className="py-24 lg:py-32 bg-[#f5f5f5]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span 
            className="text-[#0067ff] text-sm uppercase tracking-widest font-medium mb-4 block overflow-hidden"
            style={{
              opacity: isInView ? 1 : 0,
              clipPath: isInView ? 'inset(0 0 0 0)' : 'inset(0 100% 0 0)',
              transition: 'all 1.4s ease-out'
            }}
          >
            {t.contact.label}
          </span>
          <h2 
            className="text-4xl md:text-5xl font-serif font-bold text-black mb-6"
            style={{
              opacity: isInView ? 1 : 0,
              transform: isInView ? 'translateY(0) scale(1)' : 'translateY(100px) scale(0.7)',
              transition: 'all 1.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) 0.3s'
            }}
          >
            {t.contact.title}
          </h2>
          <h3 
            className="text-gray-600 text-lg font-light mb-6 max-w-2xl mx-auto"
            style={{
              opacity: isInView ? 1 : 0,
              transition: 'all 1s ease-out 0.5s'
            }}
          >
            {t.contact.subtitle}
          </h3>
          <div 
            className="h-1 bg-[#0067ff] mx-auto"
            style={{
              width: isInView ? '5rem' : '0',
              opacity: isInView ? 1 : 0,
              transition: 'all 1.2s ease-out 0.6s'
            }}
          />
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Info */}
          <div>
            {/* Location */}
            <div 
              className="flex items-start space-x-4 mb-8"
              style={{
                opacity: isInView ? 1 : 0,
                transform: isInView ? 'translateX(0)' : 'translateX(-80px)',
                transition: 'all 1.2s ease-out 0.4s'
              }}
            >
              <div className="w-14 h-14 bg-[#0067ff] rounded-full flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-black font-serif font-bold text-xl mb-2">{t.contact.location}</h3>
                <p className="text-gray-600 text-lg">{contactInfo.address}</p>
              </div>
            </div>

            {/* Phone */}
            <div 
              className="flex items-start space-x-4 mb-8"
              style={{
                opacity: isInView ? 1 : 0,
                transform: isInView ? 'translateX(0)' : 'translateX(-80px)',
                transition: 'all 1.2s ease-out 0.6s'
              }}
            >
              <div className="w-14 h-14 bg-[#0067ff] rounded-full flex items-center justify-center flex-shrink-0">
                <Phone className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-black font-serif font-bold text-xl mb-2">{t.contact.phone}</h3>
                <p className="text-gray-600 text-lg">
                  <a href={`tel:${contactInfo.phone.replace(/[^0-9+]/g, '')}`} className="hover:text-[#0067ff] transition-colors">
                    {contactInfo.phone}
                  </a>
                </p>
              </div>
            </div>

            {/* Working Hours */}
            <div 
              className="flex items-start space-x-4 mb-8"
              style={{
                opacity: isInView ? 1 : 0,
                transform: isInView ? 'translateX(0)' : 'translateX(-80px)',
                transition: 'all 1.2s ease-out 0.8s'
              }}
            >
              <div className="w-14 h-14 bg-[#0067ff] rounded-full flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-black font-serif font-bold text-xl mb-2">{t.contact.hours}</h3>
                <p className="text-gray-600 text-lg">{t.contact.weekdays}</p>
                <p className="text-gray-600 text-lg">{t.contact.saturday}</p>
              </div>
            </div>

            {/* Instagram */}
            <div 
              className="flex items-start space-x-4 mb-8"
              style={{
                opacity: isInView ? 1 : 0,
                transform: isInView ? 'translateX(0)' : 'translateX(-80px)',
                transition: 'all 1.2s ease-out 0.9s'
              }}
            >
              <a 
                href="https://www.instagram.com/studio149.rs/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-14 h-14 bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 rounded-full flex items-center justify-center flex-shrink-0 hover:scale-110 transition-transform duration-300"
                data-testid="instagram-link"
              >
                <Instagram className="w-6 h-6 text-white" />
              </a>
              <div>
                <h3 className="text-black font-serif font-bold text-xl mb-2">Instagram</h3>
                <a 
                  href="https://www.instagram.com/studio149.rs/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-600 text-lg hover:text-[#0067ff] transition-colors"
                >
                  @studio149.rs
                </a>
              </div>
            </div>

            {/* Call to Action */}
            <div 
              className="mt-10"
              style={{
                opacity: isInView ? 1 : 0,
                transform: isInView ? 'translateY(0)' : 'translateY(50px)',
                transition: 'all 1.2s ease-out 1s'
              }}
            >
              <a
                href={`tel:${contactInfo.phone.replace(/[^0-9+]/g, '')}`}
                className="inline-flex items-center bg-[#0067ff] hover:bg-[#0052cc] text-white px-6 py-3 rounded transition-all duration-300 hover:scale-105"
              >
                <Phone className="w-5 h-5 mr-2" />
                {t.contact.ctaButton}
              </a>
            </div>
          </div>

          {/* Map */}
          <div
            style={{
              opacity: isInView ? 1 : 0,
              transform: isInView ? 'translateX(0) scale(1)' : 'translateX(80px) scale(0.9)',
              transition: 'all 1.4s ease-out 0.5s'
            }}
          >
            <div className="aspect-square lg:aspect-[4/3] bg-gray-300 rounded-lg overflow-hidden shadow-xl">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2829.5!2d20.4095!3d44.8456!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x475a65d554efaaab%3A0x123456789!2sCara%20Du%C5%A1ana%20153%2C%20Zemun!5e0!3m2!1ssr!2srs!4v1700000000000!5m2!1ssr!2srs"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Studio 149 Lokacija - Cara Dušana 153, Zemun"
              />
            </div>
            <div className="mt-4 text-center">
              <a
                href="https://www.google.com/maps/search/Cara+Du%C5%A1ana+153,+Zemun"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#0067ff] hover:text-[#0052cc] font-medium transition-colors inline-flex items-center"
              >
                <MapPin className="w-4 h-4 mr-1" />
                {t.contact.mapLink}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
